/**
 * 军师对话「传图」的**唯一一份**逻辑 —— H5 与 PC 共用(2026-08-19)。
 *
 * 老板 0818 报「军师没有上传图片入口」:H5 一直有,PC 那套对话组件从来就没有过
 * (不是回归)。补 PC 的时候没有把 H5 那半复制一遍 —— 附件盘状态机、单张失败不阻断、
 * 失败计数文案、出站 refs 形状全收在这里,两端只各写各的 UI。
 *
 * ★为什么文案也要收进来:失败提示是「N 张图片上传失败」这种带计数的口径,
 * 复制一份就迟早两端不一致,而不一致的那一端不会红 —— 没人会为一句提示写跨端断言。
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { mediaProviderApi } from "@/entities/media-provider/api";

/** 对话附件盘里的一张上传图(import 成一等 asset 后的 taskId/assetId + 缩略图 URL)。 */
export interface UploadedImage {
  taskId: number;
  assetId: number;
  url: string;
}

/**
 * **「一张图的引用」在军师这条链上的唯一形状**(0829 收敛)。
 *
 * 同一个 `{taskId, assetId}` 在这条链上有两个语义位:
 *   · 出站 `body.uploadedImages` 的元素(本轮用户传的图)—— 见 {@link UploadRef};
 *   · 画布焦点图(改图工具的默认目标)—— 见 H5 `FocusAsset`。
 * ai 侧 `_resolve_target` 正是在这两者之间做回落,所以**它们必须是同一个形状**。
 *
 * ★为什么收敛:此前 PC 的 `UploadRef` 与 H5 的 `FocusAsset` 各 `export interface` 一份,
 *   H5 那份文件的注释自己就写着「两端各留一份结构相同的 interface,**改了一边不会红**」——
 *   `UploadedImage` 早就为同一个理由收敛过了,`FocusAsset` 漏了。0829 排查「图片发不出去」时
 *   这两份定义直接让人误判成"两端字段不一致",白查一轮。
 * ★守卫在 `assetRef.test.ts`,判据钉在 **tsc**(vitest 不做类型检查,类型守卫在它那里恒绿)。
 */
export interface AssetRef {
  taskId: number;
  assetId: number;
}

/**
 * 出站 `body.uploadedImages` 的元素形状(= {@link AssetRef} 的语义别名)。
 * ai 侧 turn / turn_resume 据它重建 `ctx.uploaded_images` 供 `_resolve_target` 定位
 * (ai `app/modules/advisor/api/routes.py`「S-photo:resume 重发上传图集(ctx 每次重建)」)。
 * ★走既有契约,不新造字段。
 */
export type UploadRef = AssetRef;

/** 附件盘 → 出站 refs(URL 是给人看的缩略图,不进请求体)。 */
export function toUploadRefs(imgs: UploadedImage[]): UploadRef[] {
  return imgs.map((u) => ({ taskId: u.taskId, assetId: u.assetId }));
}

/**
 * 失败计数文案。单张失败不阻断其余,所以「失败」有两档:全挂 / 挂了几张。
 * 只弹一句「失败」用户不知道该重传哪张 —— 这就是要分档的原因。
 */
export function uploadFailureText(failed: number, total: number): string | null {
  if (failed <= 0) return null;
  return failed >= total ? "图片上传失败,请重试" : `${failed} 张图片上传失败`;
}

/**
 * 附件盘的 module 级存活层 —— 与两端各自的会话缓存(PC `sessionCache` / H5 `chatCache`)同寿。
 *
 * ★ADV-UPLOAD-TRAY-1(2026-08-26):原来附件盘只是面板内一个 `useState`,
 *   而**消息和待确认卡是有 module 级缓存的**(PC 那份注释就写着「卸载再挂载 → 恢复上次会话态」)。
 *   于是面板一卸载:**对话回来了,图没回来** —— 界面上完全看不出来,下一条消息静默不带
 *   `uploadedImages`,ai 侧 `ctx.uploaded_images` 为空。
 *   PC 尤其容易撞:军师装在 Radix `<Sheet>` 里(`widgets/AdvisorLauncher`),
 *   Sheet 一关就卸载内容;H5 是整页,卸载少见但同一份逻辑同一个洞。
 *   ★恢复的**不对称**才是缺陷 —— 不是"什么都没恢复",所以判据必须同时断言
 *     「消息恢复了」和「附图也恢复了」,只断言后者分不清是修好了还是缓存整个没生效。
 */
const trayCache = new Map<string, UploadedImage[]>();

/** 测试隔离用:清空跨 mount 的附件盘缓存(与 `__clearAdvisorChatCache` 成对)。 */
export function __clearUploadTrayCache() {
  trayCache.clear();
}

/**
 * 对话附件盘:本条待发消息附的上传图(多张,可删)。
 *
 * @param sessionId 与会话缓存同一个 key —— 换会话不该串图,同会话卸载重挂不该丢图。
 */
export function useUploadTray(sessionId: string) {
  const [pendingUploads, setPendingUploads] = useState<UploadedImage[]>(
    () => trayCache.get(sessionId) ?? []
  );
  // 每次变化写回,使下一次同 sessionId 挂载能恢复(与 ChatState 那份同机理)
  useEffect(() => {
    trayCache.set(sessionId, pendingUploads);
  }, [sessionId, pendingUploads]);
  // 换会话:先把新会话的盘读回来(不是清空 —— 切回去还得在)
  useEffect(() => {
    setPendingUploads(trayCache.get(sessionId) ?? []);
  }, [sessionId]);
  const attachUpload = useCallback((img: UploadedImage) => {
    // 按 assetId 去重:同一张图重复选中不该在盘里叠两格
    setPendingUploads((prev) =>
      prev.some((p) => p.assetId === img.assetId) ? prev : [...prev, img]
    );
  }, []);
  const removeUpload = useCallback((assetId: number) => {
    setPendingUploads((prev) => prev.filter((p) => p.assetId !== assetId));
  }, []);
  const clearUploads = useCallback(() => setPendingUploads([]), []);
  return { pendingUploads, attachUpload, removeUpload, clearUploads };
}

/**
 * 选图 → 逐张 import 成一等 asset → 交给附件盘。
 *
 * 串行往返(不并发):后端 import 是重活,并发起十张会把移动端网络打满;
 * 而且串行才好逐张记失败数。上传期间调用方要禁发 —— 过早 send 会捕到未就绪的附件盘 → 丢图。
 */
export function useImagePicker(onImported: (img: UploadedImage) => void) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const pickImages = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const picked = Array.from(files); // 先快照:finally 清 input.value 会失效 live FileList
      setUploading(true);
      setUploadError(null);
      let failed = 0;
      try {
        for (const f of picked) {
          try {
            const r = await mediaProviderApi.importImage(f);
            onImported({ taskId: r.taskId, assetId: r.assetId, url: r.thumbUrl });
          } catch {
            failed += 1; // 单张失败不阻断其余
          }
        }
      } finally {
        setUploading(false);
        // 清掉 input.value,否则同一张图再选一次不触发 change
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
      setUploadError(uploadFailureText(failed, picked.length));
    },
    [onImported]
  );

  /** 入口按钮 → 打开系统文件框(真 input 隐藏,按钮才是用户看见的那个)。 */
  const openPicker = useCallback(() => fileInputRef.current?.click(), []);
  const clearUploadError = useCallback(() => setUploadError(null), []);

  return { uploading, uploadError, fileInputRef, pickImages, openPicker, clearUploadError };
}
